(() => {
  const SELECTOR = '[data-gut-ritual-section]';

  const parseFullLadder = (value) => (value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
    .map((pair) => {
      const index = pair.indexOf(':');
      return {
        qty: Number(pair.slice(0, index)),
        planId: String(pair.slice(index + 1))
      };
    })
    .filter((entry) => entry.qty > 0 && entry.planId);

  // IP-2026-07-14-change: keep Upcart as the primary drawer, but fully refresh and open Shopify's native drawer when the app is unavailable instead of leaving a stale cart count.
  const openCartDrawer = (addResponse, triggeredBy) => {
    if (window.NuoraUpcart && typeof window.NuoraUpcart.open === 'function') {
      window.NuoraUpcart.open();
      return;
    }

    // IP-2026-07-14-change: use the active Upcart app's documented public hooks before native fallback so both drawers can never open for one add.
    if (typeof window.upcartOpenCart === 'function') {
      try {
        if (typeof window.upcartRefreshCart === 'function') {
          Promise.resolve(window.upcartRefreshCart()).catch(() => {});
        }
      } catch (error) {}
      try {
        window.upcartOpenCart();
        return;
      } catch (error) {}
    }

    const nativeDrawer = document.querySelector('cart-drawer');
    if (nativeDrawer && addResponse?.sections && typeof nativeDrawer.renderContents === 'function') {
      if (typeof nativeDrawer.setActiveElement === 'function') {
        nativeDrawer.setActiveElement(triggeredBy || document.activeElement);
      }
      nativeDrawer.renderContents(addResponse);
      return;
    }

    // IP-2026-07-14-change: if an external drawer hook failed after the add, recover fresh native sections on demand instead of opening stale pre-add markup.
    if (nativeDrawer && typeof nativeDrawer.renderContents === 'function') {
      if (typeof nativeDrawer.setActiveElement === 'function') {
        nativeDrawer.setActiveElement(triggeredBy || document.activeElement);
      }
      const cartUrl = window.routes?.cart_url || '/cart';
      fetch(`${cartUrl}?sections=cart-drawer,cart-icon-bubble`)
        .then((response) => {
          if (!response.ok) throw new Error('Native cart sections request failed');
          return response.json();
        })
        .then((sections) => nativeDrawer.renderContents({ id: addResponse?.id || '', sections }))
        .catch(() => window.location.assign(cartUrl));
      return;
    }

    if (nativeDrawer && typeof nativeDrawer.open === 'function') {
      nativeDrawer.open(triggeredBy || document.activeElement);
      return;
    }

    window.location.assign(window.routes?.cart_url || '/cart');
  };

  const pauseMedia = (scope) => {
    scope.querySelectorAll('video').forEach((video) => {
      try { video.pause(); } catch (error) {}
    });
  };

  // IP-2026-07-14-change
  const hydrateMedia = (slide) => {
    if (!slide) return;
    const template = slide.querySelector('template[data-deferred-media]');
    if (!template) return;
    template.before(template.content.cloneNode(true));
    template.remove();
  };

  const initMedia = (section) => {
    const slides = Array.from(section.querySelectorAll('[data-media-slide]'));
    const thumbs = Array.from(section.querySelectorAll('[data-media-target]'));
    const prevButtons = Array.from(section.querySelectorAll('[data-media-prev]'));
    const nextButtons = Array.from(section.querySelectorAll('[data-media-next]'));
    const mediaFrame = section.querySelector('.hero-media');
    if (!slides.length) return;

    let currentIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    if (currentIndex < 0) currentIndex = 0;

    const activate = (index) => {
      const nextIndex = (index + slides.length) % slides.length;
      currentIndex = nextIndex;
      slides.forEach((slide) => {
        const active = Number(slide.dataset.mediaSlide) === nextIndex;
        // IP-2026-07-14-change
        if (active) hydrateMedia(slide);
        slide.classList.toggle('is-active', active);
        if (!active) pauseMedia(slide);
      });
      thumbs.forEach((thumb) => {
        const active = Number(thumb.dataset.mediaTarget) === nextIndex;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-current', active ? 'true' : 'false');
        if (active) {
          thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
      });
    };

    thumbs.forEach((thumb) => {
      // IP-2026-07-14-change
      const prepareMedia = () => {
        const targetIndex = Number(thumb.dataset.mediaTarget || 0);
        hydrateMedia(slides.find((slide) => Number(slide.dataset.mediaSlide) === targetIndex));
      };
      thumb.addEventListener('pointerenter', prepareMedia, { once: true });
      thumb.addEventListener('pointerdown', prepareMedia, { once: true });
      thumb.addEventListener('focus', prepareMedia, { once: true });
      thumb.addEventListener('click', () => activate(Number(thumb.dataset.mediaTarget || 0)));
    });

    prevButtons.forEach((button) => button.addEventListener('click', () => activate(currentIndex - 1)));
    nextButtons.forEach((button) => button.addEventListener('click', () => activate(currentIndex + 1)));

    if (mediaFrame && slides.length > 1) {
      let startX = 0;
      let startY = 0;
      let tracking = false;

      mediaFrame.addEventListener('pointerdown', (event) => {
        if (event.target.closest('video, iframe, button, a')) return;
        tracking = true;
        startX = event.clientX;
        startY = event.clientY;
      });

      mediaFrame.addEventListener('pointerup', (event) => {
        if (!tracking) return;
        tracking = false;
        const diffX = event.clientX - startX;
        const diffY = event.clientY - startY;
        if (Math.abs(diffX) < 36 || Math.abs(diffX) < Math.abs(diffY) * 1.25) return;
        activate(diffX < 0 ? currentIndex + 1 : currentIndex - 1);
      });

      mediaFrame.addEventListener('pointercancel', () => {
        tracking = false;
      });
    }
  };

  const initBundle = (section, root) => {
    const bundles = Array.from(section.querySelectorAll('[data-bundle]'));
    const refillToggle = section.querySelector('[data-refill-toggle]');
    const refillOption = refillToggle
      && (refillToggle.closest('[data-refill-option], .refill') || refillToggle);
    const atc = section.querySelector('[data-add-to-cart]');
    if (!bundles.length) return;

    const isSubscriptionOn = () => !refillToggle || refillToggle.getAttribute('aria-pressed') === 'true';
    const formatSaveText = (value) => {
      const raw = String(value || '').trim();
      const numeric = Number(raw.replace(/[^0-9.-]/g, ''));
      if (!Number.isFinite(numeric) || numeric <= 0) return 'One-time order';
      return 'Save ' + (raw.includes('$') ? raw : '$' + raw);
    };

    const syncBundlePrice = (bundle) => {
      const price = bundle.querySelector('[data-bundle-price]');
      const unit = bundle.querySelector('[data-bundle-unit]');
      const displayMode = bundle.dataset.priceDisplayMode || 'each';
      const subscriptionOn = isSubscriptionOn();
      const isEach = subscriptionOn && displayMode === 'each';
      const subPrice = isEach
        ? (bundle.dataset.subEach || bundle.dataset.subDisplay || bundle.dataset.subPrice || '')
        : (bundle.dataset.subTotal || bundle.dataset.subDisplay || bundle.dataset.subPrice || '');
      const onePrice = bundle.dataset.oneTotal
        || bundle.dataset.oneDisplay
        || bundle.dataset.onetimePrice
        || '';
      if (price) price.textContent = subscriptionOn ? subPrice : onePrice;
      if (unit) unit.textContent = isEach ? '/each' : 'total';
      const saveNode = bundle.querySelector('[data-bundle-save]');
      const dailyNode = bundle.querySelector('[data-bundle-daily]');
      const save = subscriptionOn ? bundle.dataset.subSave : bundle.dataset.onetimeSave;
      const daily = subscriptionOn ? bundle.dataset.dailySub : bundle.dataset.dailyOnetime;
      if (saveNode) saveNode.textContent = formatSaveText(save);
      if (dailyNode) dailyNode.textContent = daily || '';
    };

    const selectBundle = (bundle) => {
      bundles.forEach((item) => {
        const active = item === bundle;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-checked', String(active));
        syncBundlePrice(item);
      });
    };

    bundles.forEach((bundle) => {
      bundle.addEventListener('click', () => selectBundle(bundle));
      syncBundlePrice(bundle);
    });
    selectBundle(section.querySelector('[data-bundle].is-active') || bundles[bundles.length - 1]);

    if (refillOption) {
      refillOption.addEventListener('click', () => {
        const next = refillToggle.getAttribute('aria-pressed') !== 'true';
        refillToggle.setAttribute('aria-pressed', String(next));
        refillToggle.textContent = next ? '✓' : '';
        selectBundle(section.querySelector('[data-bundle].is-active') || bundles[0]);
      });
    }

    if (!atc) return;

    atc.addEventListener('click', async () => {
      const variantId = root.dataset.variantId;
      const activeBundle = section.querySelector('[data-bundle].is-active') || bundles[0];
      if (!variantId || !activeBundle) return;

      const original = atc.innerHTML;
      const quantity = Math.max(1, Number(activeBundle.dataset.quantity || 1));
      const ladderTiers = parseFullLadder(root.dataset.fullLadder);
      const ladderPlan = ladderTiers.find((tier) => tier.qty === quantity);
      const selectedPlanId = activeBundle.dataset.planId || (ladderPlan && ladderPlan.planId) || root.dataset.firstSellingPlanId || '';
      const shouldSubscribe = Boolean(isSubscriptionOn() && selectedPlanId);

      atc.disabled = true;
      atc.dataset.state = 'loading';
      atc.innerHTML = '<span>' + (atc.dataset.addingLabel || 'Adding...') + '</span><span aria-hidden="true">-&gt;</span>';

      try {
        let item = { id: Number(variantId), quantity };
        // IP-2026-07-14-change: ask Shopify for the exact native drawer/count sections in the add response so the fallback is current without a second cart request.
        const hasUpcart = (window.NuoraUpcart && typeof window.NuoraUpcart.open === 'function')
          || typeof window.upcartOpenCart === 'function';
        const nativeDrawer = hasUpcart ? null : document.querySelector('cart-drawer');
        const sectionsToRender = nativeDrawer && typeof nativeDrawer.getSectionsToRender === 'function'
          ? nativeDrawer.getSectionsToRender().map((section) => section.id)
          : null;
        const tileTiers = bundles
          .map((bundle) => {
            const qty = Math.max(1, Number(bundle.dataset.quantity || 1));
            const tier = ladderTiers.find((entry) => entry.qty === qty);
            const planId = bundle.dataset.planId || (tier && tier.planId);
            return planId ? { qty, planId: String(planId) } : null;
          })
          .filter(Boolean);

        const canConsolidate = shouldSubscribe
          && ladderTiers.length > 0
          && tileTiers.length > 0
          && window.SubCartStack
          && typeof window.SubCartStack.consolidateSubscriptionAdd === 'function';

        if (canConsolidate) {
          const built = typeof window.SubCartStack.buildOfferTiers === 'function'
            ? window.SubCartStack.buildOfferTiers(tileTiers, ladderTiers)
            : { tiers: tileTiers, planIds: tileTiers.map((tier) => tier.planId) };

          const consolidationResult = await window.SubCartStack.consolidateSubscriptionAdd({
            planIds: Array.from(new Set(built.planIds)),
            tiers: built.tiers,
            variantId: String(variantId),
            selectedQty: quantity,
            sectionsToRender,
            sectionsUrl: window.location.pathname
          });
          item.selling_plan = Number(selectedPlanId);
          openCartDrawer(consolidationResult?.addResponse, atc);
        } else {
          const payload = { id: Number(variantId), quantity };
          if (shouldSubscribe) payload.selling_plan = Number(selectedPlanId);
          if (sectionsToRender?.length) {
            payload.sections = sectionsToRender;
            payload.sections_url = window.location.pathname;
          }
          const response = await fetch(root.dataset.cartAddUrl || '/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify(payload)
          });
          if (!response.ok) throw new Error('Cart add failed');
          item = await response.json();
          openCartDrawer(item, atc);
        }

        atc.dataset.state = 'success';
        atc.innerHTML = '<span>' + (atc.dataset.successLabel || 'Added to cart') + '</span><span aria-hidden="true">OK</span>';
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { item } }));
        document.dispatchEvent(new CustomEvent('cart:add', { bubbles: true, detail: { item } }));
        window.setTimeout(() => {
          atc.disabled = false;
          atc.dataset.state = '';
          atc.innerHTML = original;
        }, 1400);
      } catch (error) {
        console.error('[gut-ritual-pdp-hero] cart add failed', error);
        atc.dataset.state = 'error';
        atc.innerHTML = '<span>' + (atc.dataset.errorLabel || 'Try again') + '</span><span aria-hidden="true">-&gt;</span>';
        window.setTimeout(() => {
          atc.disabled = false;
          atc.dataset.state = '';
          atc.innerHTML = original;
        }, 1800);
      }
    });
  };

  const initModal = (section) => {
    const open = section.querySelector('[data-label-open]');
    const modal = section.querySelector('[data-label-modal]');
    if (!open || !modal) return;
    const closeButtons = Array.from(modal.querySelectorAll('[data-label-close]'));

    const show = () => {
      const image = modal.querySelector('[data-label-image]');
      if (image && !image.getAttribute('src')) image.src = image.dataset.labelSrc || '';
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      section.classList.add('is-label-modal-open');
      const close = closeButtons[0];
      if (close) close.focus();
    };

    const hide = () => {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      section.classList.remove('is-label-modal-open');
      open.focus();
    };

    open.addEventListener('click', show);
    closeButtons.forEach((button) => button.addEventListener('click', hide));
    modal.addEventListener('click', (event) => {
      if (event.target === modal) hide();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && modal.classList.contains('is-open')) hide();
    });
  };

  const initFaq = (section) => {
    section.querySelectorAll('[data-quick-faq-more]').forEach((button) => {
      button.addEventListener('click', () => {
        const card = button.closest('.quick-faq');
        const hidden = Array.from(card ? card.querySelectorAll('.faq-extra') : []);
        const shouldOpen = hidden.some((item) => item.classList.contains('is-hidden'));
        hidden.forEach((item) => item.classList.toggle('is-hidden', !shouldOpen));
        button.setAttribute('aria-expanded', String(shouldOpen));
        button.textContent = shouldOpen ? (button.dataset.openText || 'Read less FAQ') : (button.dataset.closedText || 'Read more FAQ');
      });
    });
  };

  const initCarousels = (section) => {
    const syncActive = (target) => {
      const items = Array.from(target.children).filter((child) => child.nodeType === 1);
      if (!items.length) return;
      const viewportCenter = target.scrollLeft + (target.clientWidth / 2);
      let activeIndex = 0;
      let closestDistance = Infinity;

      items.forEach((item, index) => {
        const itemCenter = item.offsetLeft + (item.offsetWidth / 2);
        const distance = Math.abs(itemCenter - viewportCenter);
        if (distance < closestDistance) {
          closestDistance = distance;
          activeIndex = index;
        }
      });

      items.forEach((item, index) => item.classList.toggle('is-active', index === activeIndex));
      items.forEach((item, index) => {
        if (Math.abs(index - activeIndex) > 1) return;
        item.querySelectorAll('img[data-deferred-src]').forEach((image) => {
          image.src = image.dataset.deferredSrc || '';
          image.removeAttribute('data-deferred-src');
        });
      });
      const shell = target.closest('.carousel-shell');
      const dots = shell ? Array.from(shell.querySelectorAll('.mini-review-dot')) : [];
      dots.forEach((dot, index) => dot.classList.toggle('is-active', index === activeIndex));
    };

    const tracks = new Set();
    // IP-2026-07-14-change: do not let below-fold mini-review/UGC geometry reads defeat content-visibility during the LCP window on the audited route.
    const connectedTracks = new WeakSet();
    const trackObservers = new WeakMap();
    const pendingObservers = new Set();

    const connectTrack = (target) => {
      if (!target || connectedTracks.has(target)) return;
      connectedTracks.add(target);
      const observer = trackObservers.get(target);
      if (observer) {
        observer.disconnect();
        pendingObservers.delete(observer);
        trackObservers.delete(target);
      }

      let frame = 0;
      const requestSync = () => {
        window.cancelAnimationFrame(frame);
        frame = window.requestAnimationFrame(() => syncActive(target));
      };
      target.addEventListener('scroll', requestSync, { passive: true });
      syncActive(target);
    };

    section.querySelectorAll('[data-carousel-arrow]').forEach((arrow) => {
      const id = arrow.dataset.carouselTarget || '';
      const target = id ? section.querySelector('#' + CSS.escape(id)) : null;
      if (target) tracks.add(target);

      arrow.addEventListener('click', () => {
        if (!target) return;
        connectTrack(target);
        const dir = Number(arrow.dataset.carouselDir || 1);
        const first = target.firstElementChild;
        const styles = getComputedStyle(target);
        const gap = parseFloat(styles.columnGap || styles.gap) || 10;
        const distance = first ? first.getBoundingClientRect().width + gap : target.clientWidth;
        target.scrollBy({ left: dir * distance, behavior: 'smooth' });
        window.setTimeout(() => syncActive(target), 280);
      });
    });

    tracks.forEach((target) => {
      const shouldDefer = document.body?.classList.contains('ip-fast-gut-baseline')
        && !window.Shopify?.designMode
        && 'IntersectionObserver' in window;

      if (!shouldDefer) {
        connectTrack(target);
        return;
      }

      const shell = target.closest('.carousel-shell') || target;
      const observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting) || !target.isConnected) return;
        connectTrack(target);
      }, { rootMargin: '800px 0px' });
      trackObservers.set(target, observer);
      pendingObservers.add(observer);
      observer.observe(shell);
      shell.addEventListener('pointerdown', () => connectTrack(target), { once: true, passive: true });
      shell.addEventListener('focusin', () => connectTrack(target), { once: true });
    });

    // IP-2026-07-14-change: release pending observers when Theme Editor replaces this hero section.
    if (pendingObservers.size) {
      const handleSectionUnload = (event) => {
        if (!event.target?.contains?.(section) && section.isConnected) return;
        pendingObservers.forEach((observer) => observer.disconnect());
        pendingObservers.clear();
        document.removeEventListener('shopify:section:unload', handleSectionUnload);
      };
      document.addEventListener('shopify:section:unload', handleSectionUnload);
    }
  };

  const initUgc = (section) => {
    section.querySelectorAll('[data-ugc-inline]').forEach((card) => {
      const sync = (video) => {
        const playing = !video.paused && !video.ended;
        const control = card.querySelector('[data-ugc-control]');
        card.classList.toggle('is-playing', playing);
        card.classList.toggle('has-started', video.currentTime > 0 || playing);
        if (control) control.textContent = playing ? 'Pause' : 'Play';
      };

      const play = () => {
        const src = card.dataset.videoSrc;
        if (!src) return;
        let video = card.querySelector('video');
        if (!video) {
          video = document.createElement('video');
          video.src = src;
          // IP-2026-07-14-change
          const posterImage = card.querySelector('img');
          if ((posterImage && posterImage.currentSrc) || card.dataset.videoPoster) {
            video.poster = (posterImage && posterImage.currentSrc) || card.dataset.videoPoster;
          }
          video.controls = false;
          video.playsInline = true;
          video.muted = false;
          video.preload = 'metadata';
          video.setAttribute('playsinline', '');
          video.setAttribute('webkit-playsinline', '');
          video.setAttribute('aria-label', 'Customer video');
          card.appendChild(video);
          video.addEventListener('play', () => sync(video));
          video.addEventListener('pause', () => sync(video));
          video.addEventListener('ended', () => sync(video));
        }

        section.querySelectorAll('[data-ugc-inline] video').forEach((other) => {
          if (other !== video) other.pause();
        });

        if (video.paused) {
          video.muted = false;
          video.volume = Math.max(video.volume || 0, .85);
          video.play().then(() => sync(video)).catch(() => {
            video.muted = true;
            video.play().finally(() => sync(video)).catch(() => {});
          });
        } else {
          video.pause();
        }
      };

      card.addEventListener('click', play);
      if (card.tagName !== 'BUTTON') {
        card.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          play();
        });
      }
    });
  };

  // IP-2026-07-15-change: reveal the optional lightweight CTA only after the FAQ gate passes above the viewport, then navigate back to the bundle anchor without submitting a cart form.
  const initStickyBundleCta = (section) => {
    const sticky = section.querySelector('[data-sticky-bundle-cta]');
    const gate = section.querySelector('[data-sticky-bundle-gate]');
    const target = section.querySelector('[data-sticky-bundle-target]');
    const link = sticky?.querySelector('[data-sticky-bundle-link]');
    const footer = document.querySelector('footer.footer, footer[role="contentinfo"]');
    if (!sticky || !gate || !target || !link) return;

    let isPastFaq = false;
    let isFooterVisible = false;

    const setVisible = (visible) => {
      // IP-2026-07-15-change: use the original custom-sticky-atc "show" state so the rendered class and transition match the reference component 1:1.
      sticky.classList.toggle('show', visible);
      sticky.setAttribute('aria-hidden', String(!visible));
      link.tabIndex = visible ? 0 : -1;
    };

    const syncVisibility = () => setVisible(isPastFaq && !isFooterVisible);

    link.addEventListener('click', (event) => {
      event.preventDefault();
      // IP-2026-07-15-change: release focus before the observer hides the fixed bar on the upward return to the bundle.
      link.blur();
      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      target.scrollIntoView({ behavior, block: 'start' });
    });

    if ('IntersectionObserver' in window) {
      const gateObserver = new IntersectionObserver((entries) => {
        const entry = entries[0];
        if (!entry) return;
        isPastFaq = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        syncVisibility();
      });
      gateObserver.observe(gate);

      // IP-2026-07-15-change: stop the floating CTA before it can cover standard or compact footer links.
      let footerObserver = null;
      if (footer) {
        footerObserver = new IntersectionObserver((entries) => {
          isFooterVisible = Boolean(entries[0]?.isIntersecting);
          syncVisibility();
        });
        footerObserver.observe(footer);
      }

      // IP-2026-07-15-change: release the section-scoped observer when Shopify replaces the hero in Theme Editor.
      const handleSectionUnload = (event) => {
        if (event.target === section || event.target?.contains?.(section) || !section.isConnected) {
          gateObserver.disconnect();
          footerObserver?.disconnect();
          document.removeEventListener('shopify:section:unload', handleSectionUnload);
        }
      };
      document.addEventListener('shopify:section:unload', handleSectionUnload);
      return;
    }

    // IP-2026-07-15-change: old-browser fallback is passive and frame-throttled; modern storefronts stay entirely observer-driven.
    let frame = 0;
    const syncFallback = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        if (!section.isConnected) {
          window.removeEventListener('scroll', syncFallback);
          return;
        }
        const footerRect = footer?.getBoundingClientRect();
        isPastFaq = gate.getBoundingClientRect().bottom <= 0;
        isFooterVisible = Boolean(footerRect && footerRect.top < window.innerHeight && footerRect.bottom > 0);
        syncVisibility();
      });
    };
    window.addEventListener('scroll', syncFallback, { passive: true });
    syncFallback();
  };

  const initSection = (section) => {
    if (section.dataset.gutRitualInit === 'true') return;
    section.dataset.gutRitualInit = 'true';
    const root = section.querySelector('[data-gut-ritual-hero]');
    if (!root) return;
    initMedia(section);
    initBundle(section, root);
    initModal(section);
    initFaq(section);
    initCarousels(section);
    initUgc(section);
    // IP-2026-07-15-change: initialize only when the section toggle rendered the CTA markup.
    initStickyBundleCta(section);
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll(SELECTOR).forEach(initSection);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });
})();
